import { Box, Typography, useTheme, Paper, Dialog, DialogTitle, DialogContent, IconButton, Button } from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import Plot from "react-plotly.js";
import { TableHeaderContainer } from "../../components/container/TableHeaderContainer";
import { TableTitleContainer } from "../../components/container/TableTitleContainer";
import ResizableTextBox from "../../components/input/ResizableTextBox";
import { getOrgTokens, ORG_PALETTE } from "../../theme/style/organizationPalette";
import { GRAPH_PALETTE } from "../../theme/style/graphChartPalette";
import { LuEye, LuX, LuExternalLink, LuActivity } from "react-icons/lu";

function EPDLifeCycleAssessment({ firstSelectedRowData }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const tok = getOrgTokens(isDark);

  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewTitle, setPreviewTitle] = useState("");

  const inputs = [
    { label: "Reference PCR", value: firstSelectedRowData?.pcr || "", span: 4, rows: 3 },
    { label: "Declared Unit", value: firstSelectedRowData?.declared_unit || "", span: 4, rows: 3 },
    { label: "EPD URL", value: firstSelectedRowData?.original_ec3_link || "", span: 4, rows: 3 },
    { label: "LCA Software", value: firstSelectedRowData?.lcaSoftware || "", span: 4, rows: 3 },
    { label: "PCR Document", value: firstSelectedRowData?.pcr_document || "", span: 4, rows: 3 },
    { label: "LCIA Methodology", value: firstSelectedRowData?.primary_lcia_method || "", span: 4, rows: 3 },
    { label: "Cut-Off", value: "", span: 6, rows: 3 },
    { label: "Allocation Procedure", value: "", span: 6, rows: 3 },
    { label: "Primary Sources", value: "", span: 12, rows: 3 },
    { label: "Interpretation", value: "", span: 12, rows: 3 },
    { label: "References / Environmental Factors", value: "", span: 12, rows: 3 },
  ];

  const handleOpenPreview = (url, label) => {
    setPreviewUrl(url);
    setPreviewTitle(label);
  };

  const handleClosePreview = () => {
    setPreviewUrl(null);
    setPreviewTitle("");
  };

  const isUrl = (val) => {
    if (!val || typeof val !== "string") return false;
    const clean = val.trim();
    return clean.startsWith("http://") || clean.startsWith("https://");
  };

  // ─── Bar Chart Data Extraction & Normalization ───
  const indicatorKeys = [
    { key: "gwp", label: t("Carbon (GWP)") },
    { key: "ap", label: t("Acidification (AP)") },
    { key: "odp", label: t("Ozone (ODP)") },
    { key: "pocp", label: t("Smog (POCP)") },
    { key: "ep_marine", label: t("Eutrophication (EP)") }
  ];

  const selectedValues = {
    gwp: Number(firstSelectedRowData?.gwp) || 0,
    ap: Number(firstSelectedRowData?.ap) || 0,
    odp: Number(firstSelectedRowData?.odp) || 0,
    pocp: Number(firstSelectedRowData?.pocp) || 0,
    ep_marine: Number(firstSelectedRowData?.ep_marine) || 0
  };

  const averages = firstSelectedRowData?.categoryAverages || {};

  const barSelectedValues = [];
  const barLabels = [];
  const barBenchmarkValues = [];

  indicatorKeys.forEach(({ key, label }) => {
    const val = selectedValues[key];
    const avg = Number(averages[key]);
    if (val > 0 && avg > 0) {
      const pct = (val / avg) * 100;
      barSelectedValues.push(pct);
      barBenchmarkValues.push(100);
      barLabels.push(label);
    }
  });

  const showChart = barSelectedValues.length > 0;

  const chartData = [
    // Trace 1: Category Average Benchmark (Gray)
    {
      type: "bar",
      y: barLabels,
      x: barBenchmarkValues,
      orientation: "h",
      name: t("Category Average"),
      marker: {
        color: isDark ? "#4b5563" : "#cbd5e1"
      },
      hoverinfo: "text",
      text: barBenchmarkValues.map(() => `${t("Baseline Average")} (100%)`)
    },
    // Trace 2: Selected Product (Brand Green #659d33)
    {
      type: "bar",
      y: barLabels,
      x: barSelectedValues,
      orientation: "h",
      name: firstSelectedRowData?.name ? firstSelectedRowData.name.substring(0, 12) + "..." : t("Selected Product"),
      marker: {
        color: "#659d33"
      },
      hoverinfo: "text",
      text: barSelectedValues.map((pct, idx) => {
        const label = barLabels[idx];
        const actualVal = selectedValues[indicatorKeys.find(k => k.label === label)?.key] || 0;
        return `${label}: ${actualVal.toFixed(4)} (${pct.toFixed(1)}% ${t("of avg")})`
      })
    }
  ];

  const barLayout = {
    barmode: "group",
    bargap: 0.15,
    bargroupgap: 0.05,
    xaxis: {
      title: {
        text: "% " + t("of Category Average"),
        font: {
          family: GRAPH_PALETTE.font.family,
          size: 14,
          color: isDark ? "#94a3b8" : "#64748b"
        }
      },
      range: [0, Math.max(130, ...barSelectedValues) * 1.1],
      gridcolor: isDark ? "#2d2d34" : "#e2e8f0",
      zerolinecolor: isDark ? "#2d2d34" : "#e2e8f0",
      tickfont: {
        family: GRAPH_PALETTE.font.family,
        size: 13,
        color: isDark ? "#94a3b8" : "#64748b"
      }
    },
    yaxis: {
      gridcolor: "transparent",
      tickfont: {
        family: GRAPH_PALETTE.font.family,
        size: 13,
        color: isDark ? "#cbd5e1" : "#475569"
      },
      autorange: "reversed" // Keep Carbon (GWP) at the top of the chart
    },
    legend: {
      orientation: "h",
      x: 0,
      y: -0.18,
      font: {
        family: GRAPH_PALETTE.font.family,
        size: 13,
        color: isDark ? "#cbd5e1" : "#475569"
      }
    },
    margin: { t: 10, b: 40, l: 150, r: 20 },
    paper_bgcolor: "transparent",
    plot_bgcolor: "transparent",
    height: 400
  };

  return (
    <Paper variant="Table Container" sx={{ borderRadius: ORG_PALETTE.radius.table }}>
      <TableHeaderContainer>
        <TableTitleContainer>
          <Typography variant="Table Title" sx={{ fontFamily: ORG_PALETTE.font.family, fontSize: ORG_PALETTE.font.size.tableTitle, fontWeight: ORG_PALETTE.font.weight.bold }}>
            {t("Life Cycle Assessment")}
          </Typography>
        </TableTitleContainer>
      </TableHeaderContainer>
      <Box sx={{ p: 3 }}>
        {/* 1. LCA Detail Fields Panel (Always 100% Full-Width) */}
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(12, 1fr)" }, gap: 3 }}>
          {inputs.map((input, index) => {
            const hasUrl = isUrl(input.value) && input.label === "PCR Document";
            return (
              <Box key={index} sx={{ gridColumn: { xs: "span 12", md: `span ${input.span || 12}` }, display: "flex", flexDirection: "column" }}>
                <ResizableTextBox
                  label={input.label}
                  value={input.value}
                  disabled
                  placeholder={input.label}
                  minHeight={input.minHeight}
                  rows={input.rows}
                />
                {hasUrl && (
                  <Box sx={{ display: "flex", gap: 1.5, mt: 1, justifyContent: "flex-start" }}>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<LuEye size={14} />}
                      onClick={() => handleOpenPreview(input.value.trim(), input.label)}
                      sx={{
                        textTransform: "none",
                        fontSize: "12px",
                        fontFamily: ORG_PALETTE.font.family,
                        fontWeight: ORG_PALETTE.font.weight.semibold,
                        borderColor: "#659d33",
                        color: "#659d33",
                        "&:hover": {
                          borderColor: "#538229",
                          backgroundColor: "rgba(101,157,51,0.06)"
                        }
                      }}
                    >
                      {t("Preview Document")}
                    </Button>
                    <Button
                      size="small"
                      variant="text"
                      startIcon={<LuExternalLink size={13} />}
                      href={input.value.trim()}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        textTransform: "none",
                        fontSize: "12px",
                        fontFamily: ORG_PALETTE.font.family,
                        color: isDark ? "#cbd5e1" : "#475569",
                        "&:hover": {
                          color: isDark ? "#ffffff" : "#1e293b",
                          textDecoration: "underline"
                        }
                      }}
                    >
                      {t("Open in New Tab")}
                    </Button>
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>

        {/* 2. Grouped Bar Chart Section (Vertically stacked underneath) */}
        {showChart && (
          <Box sx={{ mt: 5 }}>
            {/* Clean horizontal divider line */}
            <Box sx={{ height: "1px", backgroundColor: isDark ? "#2d2d34" : "#e2e8f0", mb: 4 }} />

            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", width: "100%" }}>
              <Typography sx={{ fontFamily: ORG_PALETTE.font.family, fontWeight: ORG_PALETTE.font.weight.bold, fontSize: "18px", color: isDark ? "#ffffff" : "#0f172a", mb: 1, textAlign: "left" }}>
                {t("LCIA Profile")}
              </Typography>
              <Typography sx={{ fontFamily: ORG_PALETTE.font.family, fontSize: "13.5px", color: isDark ? "#94a3b8" : "#64748b", mb: 3, textAlign: "left" }}>
                {t("Comparing selected product impact values as a percentage against the category average baseline (100%).")}
              </Typography>

              <Box sx={{ width: "100%", height: "420px" }}>
                <Plot
                  useResizeHandler
                  style={{ width: "100%", height: "100%" }}
                  data={chartData}
                  layout={{
                    ...barLayout,
                    margin: { t: 10, b: 40, l: 155, r: 20 } // Extra margin for larger labels
                  }}
                  config={{ responsive: true, displayModeBar: false }}
                />
              </Box>
            </Box>
          </Box>
        )}
      </Box>

      {/* Modern PDF/Webpage Preview Dialog */}
      <Dialog
        open={Boolean(previewUrl)}
        onClose={handleClosePreview}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: isDark ? "#18181b" : "#ffffff",
            backgroundImage: "none",
            borderRadius: ORG_PALETTE.radius.card,
            height: "85vh",
            border: `1px solid ${isDark ? "#52525b" : "#cbd5e1"}`,
          }
        }}
      >
        <DialogTitle sx={{
          m: 0,
          p: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: `1px solid ${isDark ? "#52525b" : "#cbd5e1"}`
        }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Typography variant="h6" sx={{ fontFamily: ORG_PALETTE.font.family, fontWeight: ORG_PALETTE.font.weight.bold, fontSize: "16px", color: isDark ? "#ffffff" : "#0f172a" }}>
              {t("Preview")}: {t(previewTitle)}
            </Typography>
            {previewUrl && (
              <IconButton
                component="a"
                href={previewUrl}
                target="_blank"
                rel="noopener noreferrer"
                size="small"
                sx={{ color: isDark ? "#94a3b8" : "#64748b", "&:hover": { color: "#659d33" } }}
                title={t("Open in New Tab")}
              >
                <LuExternalLink size={16} />
              </IconButton>
            )}
          </Box>
          <IconButton
            onClick={handleClosePreview}
            sx={{
              color: isDark ? "#94a3b8" : "#64748b",
              "&:hover": { color: "#ef4444" }
            }}
          >
            <LuX size={20} />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, display: "flex", flexDirection: "column", height: "100%", overflow: "hidden", backgroundColor: isDark ? "#050505" : "#f8fafc" }}>
          {previewUrl ? (
            <iframe
              src={previewUrl}
              title={previewTitle}
              width="100%"
              height="100%"
              style={{ border: "none" }}
            />
          ) : (
            <Box sx={{ display: "flex", flex: 1, alignItems: "center", justifyContent: "center", p: 3 }}>
              <Typography sx={{ fontFamily: ORG_PALETTE.font.family, color: isDark ? "#94a3b8" : "#64748b" }}>
                {t("Loading document preview...")}
              </Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Paper>
  );
}

export default EPDLifeCycleAssessment;
